import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Eraser, RotateCcw } from 'lucide-react';

/**
 * FirmaCanvas - Componente de captura de firma táctil
 * Soporta mouse y touch para desktop y móvil
 */
function FirmaCanvas({ onFirmaChange, width = 400, height = 200 }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState(null);

  // Inicializar canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setContext(ctx);

    // Dibujar fondo blanco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Dibujar línea base
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, height - 40);
    ctx.lineTo(width - 20, height - 40);
    ctx.stroke();

    // Restaurar estilo de firma
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
  }, [width, height]);

  // Obtener coordenadas del evento (mouse o touch)
  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  // Iniciar dibujo
  const startDrawing = useCallback((e) => {
    e.preventDefault();
    if (!context) return;

    const { x, y } = getCoordinates(e);
    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
  }, [context, getCoordinates]);

  // Dibujar
  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing || !context) return;

    const { x, y } = getCoordinates(e);
    context.lineTo(x, y);
    context.stroke();
    setHasSignature(true);
  }, [isDrawing, context, getCoordinates]);

  // Terminar dibujo
  const stopDrawing = useCallback((e) => {
    e?.preventDefault();
    if (!context) return;

    context.closePath();
    setIsDrawing(false);

    // Notificar cambio
    if (hasSignature && canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onFirmaChange?.(dataUrl);
    }
  }, [context, hasSignature, onFirmaChange]);

  // Limpiar canvas
  const limpiar = useCallback(() => {
    if (!context || !canvasRef.current) return;

    const canvas = canvasRef.current;

    // Limpiar todo
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Redibujar línea base
    context.strokeStyle = '#e5e7eb';
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(20, height - 40);
    context.lineTo(width - 20, height - 40);
    context.stroke();

    // Restaurar estilo
    context.strokeStyle = '#1f2937';
    context.lineWidth = 2;

    setHasSignature(false);
    onFirmaChange?.(null);
  }, [context, width, height, onFirmaChange]);

  // Exportar firma actual
  const exportarFirma = useCallback(() => {
    if (!canvasRef.current || !hasSignature) return null;
    return canvasRef.current.toDataURL('image/png');
  }, [hasSignature]);

  // Exponer método para obtener firma
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.exportarFirma = exportarFirma;
      canvasRef.current.limpiar = limpiar;
    }
  }, [exportarFirma, limpiar]);

  return (
    <div className="firma-canvas-container">
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="border-2 border-gray-300 rounded-lg cursor-crosshair touch-none bg-white"
          style={{ maxWidth: '100%', height: 'auto' }}
          // Mouse events
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          // Touch events
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />

        {/* Texto de ayuda */}
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-400 text-sm">Firme aquí</p>
          </div>
        )}
      </div>

      {/* Botones de acción */}
      <div className="flex justify-end gap-2 mt-2">
        <button
          type="button"
          onClick={limpiar}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={!hasSignature}
        >
          <RotateCcw className="w-4 h-4" />
          Limpiar
        </button>
      </div>

      {/* Indicador de estado */}
      <p className={`text-xs mt-1 ${hasSignature ? 'text-green-600' : 'text-gray-400'}`}>
        {hasSignature ? 'Firma capturada' : 'Dibuje su firma en el recuadro'}
      </p>
    </div>
  );
}

export default FirmaCanvas;
